'use client';

import { cn, formatDate, formatTruncatedText } from '@/helpers/common';
import { TJobListElement } from '@/ts/jobs';
import { getOccupationTitle, getSeniorityTitle } from '@/helpers/taxonomy';
import React from 'react';
import { ROUTES } from '@/constants/routes/default';
import Link from 'next/link';
import StubKG from './stubs/StubKG';
import { STUB_DEFAULT_TEXTS, STUB_ERROR_TEXTS } from '@/constants/kg/default';
import Tooltip from '../Tooltip';

type JobsListKGProps = {
  jobs: TJobListElement[];
  isError?: boolean;
  withHeader?: boolean;
};

type TMetaItem = { key: string; value: string; className?: string };

const CHIP_LABEL_MAX_LENGTH = 200;

const getLocationLabel = (job: TJobListElement) => {
  const city = job.city?.trim();
  const state = job.state?.trim();

  if (city && state) return `${city} (${state})`;
  if (city || state) return city || state || '';
  if (job.isRemote) return 'Remote';

  return '';
};

const getSeniorityLabel = (code?: string) => {
  const title = getSeniorityTitle(code);

  return title && code ? `${title} ${code.toUpperCase()}` : '';
};

const JobsListKG = ({ jobs, isError = false, withHeader = false }: JobsListKGProps) => {
  if (!jobs.length) {
    return (
      <StubKG
        texts={isError ? STUB_ERROR_TEXTS : STUB_DEFAULT_TEXTS}
        elementClassName={{ mainClassName: 'mt-14' }}
      />
    );
  }

  return (
    <ul
      className="flex flex-col rounded-xs border border-kg-athens-gray divide-y divide-kg-athens-gray bg-white"
      id="jobs-list"
    >
      {withHeader && (
        <li
          aria-hidden="true"
          className="flex items-center gap-1.5 py-3.75 px-3 bg-kg-athens-gray-light rounded-t-xs"
        >
          <div className="w-2 h-2 rounded-full bg-kg-valencia" />
          <div className="w-2 h-2 rounded-full bg-kg-hot-toddy" />
          <div className="w-2 h-2 rounded-full bg-kg-fun-blue" />
        </li>
      )}
      {jobs.map((job) => {
        const dateLabel = formatDate({
          dateValue: job.createdAt,
          variant: 4,
        });
        const occupationTitle = getOccupationTitle(job.occ_1);
        const metaItems = [
          job.company
            ? {
                key: 'company',
                value: job.company,
                className: 'font-medium capitalize text-kg-black-pearl',
              }
            : null,
          { key: 'location', value: getLocationLabel(job) },
          { key: 'seniority', value: getSeniorityLabel(job.seniority) },
        ].filter((item): item is TMetaItem => Boolean(item?.value));

        return (
          <li key={job.id} className="p-5 bg-white first:rounded-t-xs last:rounded-b-xs">
            <div className="flex items-start justify-between gap-4">
              <Link
                href={`${ROUTES.jobs}/${job.id}`}
                className="block font-bold text-kg-fun-blue no-underline hover:underline"
              >
                {job.title}
              </Link>
              <p
                className={cn(
                  'shrink-0 text-xs mt-1',
                  dateLabel === 'today' || dateLabel === 'yesterday'
                    ? 'text-kg-hot-toddy capitalize'
                    : 'text-kg-mako',
                )}
              >
                {dateLabel}
              </p>
            </div>
            {metaItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm mt-1 text-kg-mako">
                {metaItems.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 && (
                      <span className="inline-block h-0.5 w-0.5 shrink-0 self-center rounded-full bg-kg-mako" />
                    )}
                    <span className={item.className}>{item.value}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
            {job.salary?.raw && (
              <p className="mt-1.5 text-sm font-bold text-kg-black-pearl">{job.salary.raw}</p>
            )}
            {occupationTitle && (
              <div className="mt-1.5 flex max-w-full">
                <Tooltip
                  className="max-w-70 whitespace-normal wrap-break-word rounded-none"
                  tooltip={occupationTitle}
                >
                  <span className="min-w-0 max-w-full shrink overflow-hidden rounded-xs border border-kg-athens-gray bg-kg-athens-gray-light px-2 py-1 text-xs text-kg-mako">
                    <span className="block truncate">
                      {formatTruncatedText({
                        text: occupationTitle,
                        maxLength: CHIP_LABEL_MAX_LENGTH,
                      })}
                    </span>
                  </span>
                </Tooltip>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default JobsListKG;
